import type { ReactNode } from 'react';

import { useState, useContext, createContext } from 'react';

import { cn } from '@shared/utils/cn';

type SlotContextValue = {
  target: HTMLElement | null;
  setTarget: (element: HTMLElement | null) => void;
};

const PageHeaderSlotContext = createContext<SlotContextValue | null>(null);

/** Lets a layout expose a spot in its header where pages render their title + breadcrumb (see `WarehouseBreadcrumbs`). */
export const PageHeaderSlotProvider = ({ children }: { children: ReactNode }) => {
  const [target, setTarget] = useState<HTMLElement | null>(null);

  return <PageHeaderSlotContext.Provider value={{ target, setTarget }}>{children}</PageHeaderSlotContext.Provider>;
};

/** The header element the page title is portaled into, `null` outside a layout providing the slot. */
export const usePageHeaderTarget = () => useContext(PageHeaderSlotContext)?.target ?? null;

/** Mount point rendered by the header; takes the remaining width and lets the title truncate. */
export const PageHeaderSlot = ({ className }: { className?: string }) => {
  const context = useContext(PageHeaderSlotContext);

  return <div className={cn('min-w-0 flex-1', className)} data-slot="page-header-slot" ref={context?.setTarget} />;
};
